// EXTERNAL DEPENDENCIES
import React from 'react'

// HELPERS
import { usePrefixedContent } from '@/utils'
import { useFormattedAddresses } from '@/hooks'

// COMPONENTS
import {
  BodyText,
  EmailIcon,
  Link,
  LocationIcon,
  Phone2Icon,
  Row,
  WebsiteIcon,
} from '@paypalcorp/pp-react'
import { WithLoadingShimmer } from '@/common/components/loading/with-loading-shimmer'

// TEST IDS
import {
  ADDRESS_ICON_SECTION_TEST_ID,
  EMAIL_ICON_SECTION_TEST_ID,
  PHONE_NUMBER_ICON_SECTION_TEST_ID,
  PROFILE_CARD_BUSINESS_ADDRESS_TEST_ID,
  WEBSITE_ICON_SECTION_TEST_ID,
} from '@/constants/test-ids'

// TYPES
import { LocationDisplayPreference, PortableAddress } from '@/types'

// STYLES
import { mainBrandPrimaryBlueText } from '@/styles/colors'
import styles from './styles.module.less'

/**
 * Displays the contact info the seller wishes to display
 */
export default function ContactInfoSection({
  businessAddress,
  contactInfo,
  isLoading,
  interactionsDisabled,
  selectedLocationDisplayPreference,
}: Readonly<{
  businessAddress: PortableAddress
  contactInfo: {
    businessEmail?: string
    businessPhoneNumber?: string
    businessWebsiteUrl?: string
  }
  isLoading: boolean
  interactionsDisabled: boolean
  selectedLocationDisplayPreference: LocationDisplayPreference
}>) {
  const { businessEmail, businessPhoneNumber, businessWebsiteUrl } =
    contactInfo || {}

  // INITIALIZE CONTENT
  const contentPrefix = 'components.ui.profile.profile-card.'
  const getContent = usePrefixedContent(contentPrefix)

  // Formatted addresses
  const formattedAddresses = useFormattedAddresses(businessAddress)
  const formattedAddress = formattedAddresses[selectedLocationDisplayPreference]

  return (
    <>
      <Row noGutters>
        <BodyText isLoading={isLoading} strong>
          {getContent('contactInfo')}
        </BodyText>
      </Row>
      <Row noGutters className={styles.iconSectionContainer}>
        {businessPhoneNumber ? (
          <WithLoadingShimmer isLoading={isLoading}>
            <Row
              noGutters
              className={styles.iconSection}
              data-testid={PHONE_NUMBER_ICON_SECTION_TEST_ID}
            >
              <Phone2Icon
                aria-hidden
                color={mainBrandPrimaryBlueText}
                size='sm'
              />
              <BodyText>{businessPhoneNumber}</BodyText>
            </Row>
          </WithLoadingShimmer>
        ) : null}
        {businessEmail ? (
          <WithLoadingShimmer isLoading={isLoading}>
            <Row
              noGutters
              className={styles.iconSection}
              data-testid={EMAIL_ICON_SECTION_TEST_ID}
            >
              <EmailIcon
                aria-hidden
                color={mainBrandPrimaryBlueText}
                size='sm'
              />
              <Link
                className={styles.breakAll}
                disabled={interactionsDisabled}
                tabIndex={interactionsDisabled ? -1 : 0}
                href={`mailto:${businessEmail}`}
              >
                {businessEmail}
              </Link>
            </Row>
          </WithLoadingShimmer>
        ) : null}
        {formattedAddress ? (
          <WithLoadingShimmer isLoading={isLoading}>
            <Row
              noGutters
              className={styles.iconSection}
              data-testid={ADDRESS_ICON_SECTION_TEST_ID}
            >
              <LocationIcon
                aria-hidden
                color={mainBrandPrimaryBlueText}
                size='sm'
              />
              <BodyText
                className={styles.breakWord}
                data-testid={PROFILE_CARD_BUSINESS_ADDRESS_TEST_ID}
              >
                {formattedAddress}
              </BodyText>
            </Row>
          </WithLoadingShimmer>
        ) : null}
        {businessWebsiteUrl ? (
          <WithLoadingShimmer isLoading={isLoading}>
            <Row
              noGutters
              className={styles.iconSection}
              data-testid={WEBSITE_ICON_SECTION_TEST_ID}
            >
              <WebsiteIcon
                aria-hidden
                color={mainBrandPrimaryBlueText}
                size='sm'
              />
              <Link
                tabIndex={interactionsDisabled ? -1 : 0}
                target='_blank'
                href={businessWebsiteUrl}
              >
                {businessWebsiteUrl}
              </Link>
            </Row>
          </WithLoadingShimmer>
        ) : null}
      </Row>
    </>
  )
}
